import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowDown, ArrowUpRight, Star } from "lucide-react";
import { BRAND, HERO, waLink } from "@/data/content";

export const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start start", "end start"],
  });
  const bgY = useTransform(scrollYProgress, [0, 1], ["0%", "35%"]);
  const bgScale = useTransform(scrollYProgress, [0, 1], [1.05, 1.2]);
  const contentY = useTransform(scrollYProgress, [0, 1], ["0%", "18%"]);

  return (
    <section
      id="top"
      ref={ref}
      data-testid="hero-section"
      className="relative flex min-h-[100svh] flex-col justify-end overflow-hidden pb-16 pt-32 sm:pb-20"
    >
      <motion.div
        style={{ y: bgY, scale: bgScale }}
        className="absolute inset-0 -z-10"
      >
        <img
          src="https://images.unsplash.com/photo-1734562520377-357b990aa9ab?crop=entropy&cs=srgb&fm=jpg&q=85&w=2000"
          alt=""
          className="h-full w-full object-cover opacity-40"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/70 via-black/60 to-[#0a0a0a]" />
        <div className="absolute -left-40 top-1/3 h-[500px] w-[500px] rounded-full bg-yellow-400/20 blur-[140px]" />
      </motion.div>

      <motion.div
        style={{ y: contentY }}
        className="mx-auto w-full max-w-[1400px] px-5 sm:px-8"
      >
        <div className="mb-8 flex animate-[fade-up_0.6s_2.3s_both] items-center gap-3">
          <span className="flex items-center gap-1.5 rounded-full border border-yellow-400/40 px-3 py-1.5 font-mono-accent text-[10px] uppercase tracking-[0.25em] text-yellow-400">
            <Star size={11} className="fill-yellow-400" />
            {HERO.badge}
          </span>
        </div>

        <h1 className="font-display text-[15vw] uppercase leading-[0.86] text-white sm:text-[11vw] lg:text-[8.5vw]">
          <span className="block overflow-hidden">
            <span className="block animate-[hero-up_0.9s_2.35s_cubic-bezier(0.22,1,0.36,1)_both]">
              {BRAND.tagline[0]}
            </span>
          </span>
          <span className="block overflow-hidden">
            <span className="block animate-[hero-up_0.9s_2.5s_cubic-bezier(0.22,1,0.36,1)_both]">
              to <span className="italic text-yellow-400">Converting</span>{" "}
              them.
            </span>
          </span>
        </h1>

        <div className="mt-10 flex flex-col items-start gap-8 border-t border-white/10 pt-8 sm:flex-row sm:items-center sm:justify-between">
          <p className="max-w-md animate-[fade-up_0.7s_2.75s_both] text-base leading-relaxed text-neutral-300">
            {HERO.subtext}
          </p>

          <a
            href={waLink()}
            target="_blank"
            rel="noreferrer"
            data-testid="hero-book-button"
            className="group inline-flex animate-[fade-up_0.7s_2.9s_both] items-center gap-3 rounded-full bg-yellow-400 px-7 py-4 text-base font-semibold text-black transition-[transform,box-shadow] duration-300 hover:scale-[1.03] hover:shadow-[0_0_40px_rgba(250,204,21,0.55)]"
          >
            {HERO.button}
            <span className="grid h-7 w-7 place-items-center rounded-full bg-black text-yellow-400 transition-transform duration-300 group-hover:rotate-45">
              <ArrowUpRight size={16} />
            </span>
          </a>
        </div>
      </motion.div>

      <div className="mx-auto mt-14 flex w-full max-w-[1400px] animate-[fade-up_0.8s_3.1s_both] items-center gap-2 px-5 text-neutral-500 sm:px-8">
        <ArrowDown size={16} className="animate-bounce" />
        <span className="font-mono-accent text-[10px] uppercase tracking-[0.3em]">
          Scroll to see the proof
        </span>
      </div>
    </section>
  );
};
